import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { BIO_PARAGRAPHS, PROFILE, STATS, IMAGES } from '../mock/data';

const BIO_IMG = IMAGES.courtroom;

const Biography = () => {
  const { t } = useLanguage();

  return (
    <section id="biography" className="section relative paper-texture">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* LEFT: Atmospheric image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="lg:col-span-5"
          >
            <div className="relative portrait-frame">
              <div className="chiaroscuro relative overflow-hidden" style={{ aspectRatio: '4/5' }}>
                <img src={BIO_IMG} alt="KC Redding in the courtroom" className="w-full h-full object-cover" style={{ objectPosition: 'center 30%' }} data-testid="bio-image" />
              </div>
              <div className="absolute -top-4 -left-4 text-[10px] tracking-[0.4em] text-[#C4A35A] uppercase bg-[#0A0E1A] px-2 py-1 border border-[rgba(196,163,90,0.3)]">
                Fiat Justitia
              </div>
            </div>

            {/* Three stats */}
            <div className="grid grid-cols-1 gap-5 mt-10">
              {STATS.map((s) => (
                <div key={s.label} className="border-l-2 border-[#C4A35A] pl-5 py-2">
                  <div className="text-[10px] tracking-[0.4em] text-[#8a7a54] uppercase mb-1">
                    {s.label}
                  </div>
                  <div className="font-heading text-lg text-[#F0EDE8]">{s.value}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT: Bio text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, delay: 0.15, ease: 'easeOut' }}
            className="lg:col-span-7"
          >
            <div className="label-small mb-4">{t.bio.label}</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#F0EDE8] leading-[1.05]">
              {t.bio.heading}
            </h2>
            <div className="gold-rule mt-6 mb-8" />

            <div className="space-y-5 text-[#DCCFAD] leading-relaxed text-[15px] md:text-base">
              {BIO_PARAGRAPHS.map((p, i) => (
                <p key={i} className={i === 0 ? 'first-letter:font-heading first-letter:text-5xl first-letter:text-[#C4A35A] first-letter:mr-2 first-letter:float-left first-letter:leading-none' : ''}>
                  {p}
                </p>
              ))}
            </div>

            {/* Specialty tags */}
            <div className="mt-10">
              <div className="text-[10px] tracking-[0.4em] text-[#8a7a54] uppercase mb-3">
                {t.bio.tagsLabel}
              </div>
              <div className="flex flex-wrap gap-2">
                {PROFILE.specialties.map((s) => (
                  <span key={s} className="tag-pill">{s}</span>
                ))}
              </div>
            </div>

            {/* Languages */}
            <div className="mt-8">
              <div className="text-[10px] tracking-[0.4em] text-[#8a7a54] uppercase mb-3">
                {t.bio.languagesLabel}
              </div>
              <div className="flex flex-wrap gap-4">
                {PROFILE.languages.map((l) => (
                  <div key={l.code} className="flex items-center gap-3 border border-[rgba(196,163,90,0.2)] px-4 py-2.5 bg-[rgba(196,163,90,0.03)]">
                    <span className="text-xl leading-none">{l.flag}</span>
                    <div>
                      <div className="text-sm text-[#F0EDE8] font-medium">{l.label}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#8a7a54]">{l.level}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Biography;
